import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  School,
  Work,
  CalendarToday,
  LocationOn,
  Star,
  ArrowForward,
  CheckCircle,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getTutorByUser, clearTutor } from '../../state/slices/tutor';
import { getTutorProfileCompletionPercentage } from '../../utils/tutorProfileCompletion';
import './TutorProfileDashboard.css';

function TutorProfileDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userState = useSelector((state) => state.user).user;
  const tutorState = useSelector((state) => state.tutor);

  useEffect(() => {
    if (userState && userState.roles.includes('TUTOR') && !tutorState.tutor && !tutorState.loading) {
      dispatch(getTutorByUser(userState.id));
    }
  }, [userState, dispatch, tutorState.tutor, tutorState.loading]);
  
  // Handle "Tutor not found" gracefully - it's expected for new tutors
  useEffect(() => {
    if (tutorState.error === 'Tutor not found') {
      // Tutor profile doesn't exist yet - this is fine, just clear the error
      dispatch(clearTutor());
    }
  }, [tutorState.error, dispatch]);

  const tutor = tutorState.tutor;
  const firstName = userState?.firstName || '';
  const lastName = userState?.lastName || '';
  const joinDate = tutor?.createdAt 
    ? new Date(tutor.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : '';

  // Calculate reviews count - handle both populated reviews array and totalReviews number
  const reviewsCount = tutor?.reviews 
    ? (Array.isArray(tutor.reviews) ? tutor.reviews.length : (tutor.totalReviews || 0))
    : (tutor?.totalReviews || 0);

  // Check if calendar section has inactive status
  const isCalendarInactive = tutor?.currentStatus === t('tutorProfile.inActive') || tutor?.currentStatus === 'In Active';
  const hasAnyTutoringType =
    Array.isArray(tutor?.tutoringType) &&
    (tutor.tutoringType.includes('REMOTE') || tutor.tutoringType.includes('IN_PERSON'));

  // Calendar section is complete only when status is active and at least
  // one tutoring type (Online or In Person) is enabled.
  const isCalendarComplete = !!tutor?.currentStatus && !isCalendarInactive && hasAnyTutoringType;

  // Check completion status for each section (excluding reviews)
  const checkSectionComplete = (sectionId) => {
    switch (sectionId) {
      case 'education':
        return !!tutor?.education?.universityName;
      case 'experience':
        return !!tutor?.experience;
      case 'calendar':
        return isCalendarComplete;
      case 'location':
        return !!(tutor?.location && tutor?.rate);
      default:
        return false;
    }
  };

  // Calculate completion percentage (excluding reviews)
  const completionPercentage = useMemo(
    () => getTutorProfileCompletionPercentage(tutor, t),
    [tutor, t]
  );

  // Check if profile is complete but not approved
  const isCompleteButNotApproved = completionPercentage === 100 && tutor?.approved === 'PENDING';

  const getIconComponent = (iconName) => {
    const iconProps = { style: { fontSize: '40px', width: '40px', height: '40px' } };
    switch (iconName) {
      case 'School':
        return <School {...iconProps} />;
      case 'Work':
        return <Work {...iconProps} />;
      case 'CalendarToday':
        return <CalendarToday {...iconProps} />;
      case 'LocationOn':
        return <LocationOn {...iconProps} />;
      case 'Star':
        return <Star {...iconProps} />;
      default:
        return null;
    }
  };

  const profileSections = [
    {
      id: 'education',
      title: t('tutorProfile.education'),
      icon: 'School',
      path: '/tutor-profile/education',
      description: tutor?.education?.universityName 
        ? `${tutor.education.universityName} - ${tutor.education.department}`
        : t('tutorProfileDashboard.notCompleted'),
      isComplete: checkSectionComplete('education'),
    },
    {
      id: 'experience',
      title: t('tutorProfile.experienceAndAvailability'),
      icon: 'Work',
      path: '/tutor-profile/experience',
      description: tutor?.experience 
        ? tutor.experience.substring(0, 50) + (tutor.experience.length > 50 ? '...' : '')
        : t('tutorProfileDashboard.notCompleted'),
      isComplete: checkSectionComplete('experience'),
    },
    {
      id: 'calendar',
      title: t('tutorProfileDashboard.calendarAvailability'),
      icon: 'CalendarToday',
      path: '/tutor-profile/calendar',
      description: tutor?.currentStatus
        ? tutor.currentStatus
        : t('tutorProfileDashboard.notCompleted'),
      isComplete: checkSectionComplete('calendar'),
      isInactive: isCalendarInactive,
    },
    {
      id: 'location',
      title: t('tutorProfile.locationAndRate'),
      icon: 'LocationOn',
      path: '/tutor-profile/location',
      description: tutor?.location && tutor?.rate
        ? `${tutor.location} - ${tutor.rate} ETB/hr`
        : t('tutorProfileDashboard.notCompleted'),
      isComplete: checkSectionComplete('location'),
    },
    {
      id: 'reviews',
      title: t('tutorProfileDashboard.reviews'),
      icon: 'Star',
      path: '/tutor-profile/reviews',
      description: reviewsCount > 0
        ? `${reviewsCount} ${reviewsCount === 1 ? t('tutorProfileDashboard.review') : t('tutorProfileDashboard.reviews')}`
        : t('tutorProfileDashboard.noReviewsYet'),
      disabled: reviewsCount === 0,
      isComplete: true, // Reviews are not part of completion check
    },
  ];

  const handleCardClick = (section) => {
    if (!section.disabled) {
      dispatch(clearTutor());
      navigate(section.path);
    }
  };

  const getCardClassName = (section) => {
    let className = 'profile-card';
    if (section.disabled) {
      className += ' profile-card-disabled';
    } else if (tutorState.loading) {
      // Don't apply incomplete/complete styling while loading
      // Just return base class
    } else if (!section.isComplete || (section.id === 'calendar' && section.isInactive)) {
      className += ' profile-card-incomplete';
    } else {
      className += ' profile-card-complete';
    }
    return className;
  };

  return (
    <div className="tutor-profile-dashboard">
      {/* Custom Container Wrapper */}
      <div className="tutor-profile-container">
        {/* Banner for complete but not approved profile */}
        {isCompleteButNotApproved && (
          <div className="alert-banner">
            <CheckCircle className="alert-icon" style={{ fontSize: '24px' }} />
            <div className="alert-content">
              <div className="alert-title">
                {t('tutorProfileDashboard.profileComplete')}
              </div>
              <div className="alert-message">
                {t('tutorProfileDashboard.underReview')}
              </div>
            </div>
          </div>
        )}

        {/* Welcome Header with Progress Indicator */}
        <div className="welcome-section">
          <h1 className="welcome-title">
            {t('tutorProfileDashboard.welcome', { name: firstName })}
          </h1>
          <p className="welcome-subtitle">
            {tutor 
              ? t('tutorProfileDashboard.updateProfile') 
              : t('tutorProfileDashboard.getStarted')}
          </p>

          {/* Profile Info */}
          <div className="profile-info">
            <div className="profile-avatar">
              {firstName?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <h2 className="profile-name">
                {firstName} {lastName}
              </h2>
              <p className="profile-joined">
                {t('tutorProfileDashboard.joined')} {joinDate}
              </p>
            </div>
          </div>
        </div>

        {/* Profile Sections Grid - Custom CSS Grid Layout */}
        <div className="profile-grid">
          {profileSections.map((section) => (
            <div
              key={section.id}
              className={getCardClassName(section)}
              onClick={() => handleCardClick(section)}
            >
              <div className="card-content">
                <div className="card-header">
                  <div className="card-icon">
                    {getIconComponent(section.icon)}
                  </div>
                  {!section.disabled && (
                    <div className="card-arrow">
                      <ArrowForward style={{ fontSize: '24px', width: '24px', height: '24px' }} />
                    </div>
                  )}
                </div>
                <h3 className="card-title">
                  {section.title}
                </h3>
                <p className="card-description">
                  {section.description}
                </p>
                {section.id === 'reviews' && reviewsCount === 0 && (
                  <p className="card-note">
                    {t('tutorProfileDashboard.checkBackAfterFirstSession')}
                  </p>
                )}
              </div>
            </div>
          ))}
          {/* Progress Indicator Card - Last */}
          <div className="progress-card">
            <h3 className="progress-title">
              {t('tutorProfileDashboard.profileProgress')}
            </h3>
            <div className="progress-circle-wrapper">
              <svg width="100" height="100" viewBox="0 0 140 140" style={{ transform: 'rotate(-90deg)' }}>
                {/* Background circle */}
                <circle
                  cx="70"
                  cy="70"
                  r="60"
                  fill="none"
                  stroke="rgba(211, 47, 47, 0.2)"
                  strokeWidth="20"
                />
                {/* Completed portion (green) */}
                <circle
                  cx="70"
                  cy="70"
                  r="60"
                  fill="none"
                  stroke="#2e7d32"
                  strokeWidth="20"
                  strokeDasharray={`${(completionPercentage / 100) * 377} 377`}
                  strokeLinecap="round"
                />
                {/* Remaining portion (red) */}
                {completionPercentage < 100 && (
                  <circle
                    cx="70"
                    cy="70"
                    r="60"
                    fill="none"
                    stroke="#d32f2f"
                    strokeWidth="20"
                    strokeDasharray={`${((100 - completionPercentage) / 100) * 377} 377`}
                    strokeDashoffset={`-${(completionPercentage / 100) * 377}`}
                    strokeLinecap="round"
                  />
                )}
              </svg>
              <div className="progress-circle-center">
                <div className="progress-percentage">
                  {completionPercentage}%
                </div>
              </div>
            </div>
            <div className="progress-legend">
              <div className="progress-legend-item">
                <div className="progress-dot progress-dot-success"></div>
                <span className="progress-legend-text">
                  {t('tutorProfileDashboard.completed')}: {completionPercentage}%
                </span>
              </div>
              <div className="progress-legend-item">
                <div className="progress-dot progress-dot-error"></div>
                <span className="progress-legend-text">
                  {t('tutorProfileDashboard.remaining')}: {100 - completionPercentage}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TutorProfileDashboard;
